// Service pour communiquer avec l'API du serveur
import { prepareClientForSave } from './customFields/customFieldsHelper';
import bastionAuth from '../services/bastionAuthService';

// Fonction utilitaire pour créer les headers avec authentification
const getAuthHeaders = () => {
  return bastionAuth.getAuthHeaders();
};

// Fonction pour récupérer tous les clients
export const getAllClients = async () => {
  try {
    const response = await fetch('/api/clients', {
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        bastionAuth.handleAuthError({ status: response.status });
        return [];
      }
      throw new Error('Erreur lors de la récupération des clients');
    }
    return await response.json();
  } catch (error) {
    console.error('Erreur:', error);
    return [];
  }
};

// Fonction pour récupérer un client par son ID
export const getClientById = async (id) => {
  try {
    const response = await fetch(`/api/clients/${id}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        bastionAuth.handleAuthError({ status: response.status });
        return null;
      }
      if (response.status === 404) {
        return null;
      }
      throw new Error('Erreur lors de la récupération du client');
    }
    return await response.json();
  } catch (error) {
    console.error('Erreur:', error);
    return null;
  }
};

// Fonction pour ajouter un client
export const addClient = async (client) => {
  try {
    // Préparer le client pour la sauvegarde en traitant les champs personnalisés
    const preparedClient = prepareClientForSave(client);
    
    const response = await fetch('/api/clients', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(preparedClient),
    });
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        bastionAuth.handleAuthError({ status: response.status });
        return null;
      }
      throw new Error('Erreur lors de l\'ajout du client');
    }
    return await response.json();
  } catch (error) {
    console.error('Erreur:', error);
    return null;
  }
};

// Fonction pour mettre à jour un client
export const updateClient = async (id, client) => {
  try {
    // Récupérer les données originales du client pour préserver les mises à jour
    const originalClient = await getClientById(id);
    
    // Déboguer les données envoyées
    console.log('Données client originales envoyées à updateClient:', JSON.stringify(client));
    
    // Importer le service de préservation des données
    const { preserveCriticalData, validateDataPreservation } = await import('./updatePreservation/dataPreservationService');
    
    // Préserver les données critiques (mises à jour, serveurs, modifications locales)
    const preparedClient = preserveCriticalData(client, originalClient);
    
    // Valider que les données ont été correctement préservées
    const validation = validateDataPreservation(preparedClient, originalClient);
    
    if (!validation.success) {
      console.error('❌ Erreurs de préservation des données:', validation.errors);
      // On peut choisir de continuer ou d'arrêter selon la criticité
      // Pour l'instant, on log les erreurs mais on continue
    }
    
    if (validation.warnings.length > 0) {
      console.log('⚠️  Avertissements de préservation:', validation.warnings);
    }
    
    // Déboguer les données préparées
    console.log('Données client préparées pour l\'API (avec préservation):', JSON.stringify(preparedClient));
    
    const response = await fetch(`/api/clients/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(preparedClient),
    });
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        bastionAuth.handleAuthError({ status: response.status });
        return null;
      }
      throw new Error('Erreur lors de la mise à jour du client');
    }
    return await response.json();
  } catch (error) {
    console.error('Erreur:', error);
    return null;
  }
};

// Fonction pour supprimer un client
export const deleteClient = async (id) => {
  try {
    const response = await fetch(`/api/clients/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        bastionAuth.handleAuthError({ status: response.status });
        return false;
      }
      throw new Error('Erreur lors de la suppression du client');
    }
    return await response.json();
  } catch (error) {
    console.error('Erreur:', error);
    return false;
  }
};

// Fonction pour vérifier si l'utilisateur est connecté
export const verifyAuth = async () => {
  return await bastionAuth.verifyToken();
};

// Fonction pour se déconnecter
export const logout = () => {
  bastionAuth.logout();
};

// Fonction pour obtenir l'utilisateur actuel
export const getCurrentUser = async () => {
  return await bastionAuth.getCurrentUser();
};

// Récupérer les seuils de version
export const getVersionThresholds = async () => {
  try {
    const response = await fetch('/api/version-thresholds', {
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        bastionAuth.handleAuthError({ status: response.status });
        return {};
      }
      throw new Error('Erreur lors de la récupération des seuils');
    }
    return await response.json();
  } catch (error) {
    console.error('Erreur API getVersionThresholds:', error);
    return {};
  }
};

// Sauvegarder les seuils de version
export const saveVersionThresholds = async (thresholds) => {
  try {
    const response = await fetch('/api/version-thresholds', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(thresholds)
    });
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        bastionAuth.handleAuthError({ status: response.status });
        return null;
      }
      throw new Error('Erreur lors de la sauvegarde des seuils');
    }
    return await response.json();
  } catch (error) {
    console.error('Erreur API saveVersionThresholds:', error);
    throw error;
  }
};

// Récupérer les statistiques des mises à jour
export const getUpdateStatistics = async () => {
  try {
    const clients = await getAllClients();
    const stats = {
      totalUpdates: 0,
      completedUpdates: 0,
      failedUpdates: 0,
      inProgressUpdates: 0,
      plannedUpdates: 0,
      updatesByMonth: {},
      updatesByClient: {},
      componentUpdates: {},
      recentUpdates: []
    };
    
    const now = new Date();
    const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, 1);
    
    clients.forEach(client => {
      if (client.updates && Array.isArray(client.updates)) {
        stats.updatesByClient[client.name] = {
          total: client.updates.length,
          completed: 0,
          failed: 0,
          inProgress: 0
        };
        
        client.updates.forEach(update => {
          stats.totalUpdates++;
          
          // Compter par statut
          switch (update.status) {
            case 'completed':
              stats.completedUpdates++;
              stats.updatesByClient[client.name].completed++;
              break;
            case 'failed':
              stats.failedUpdates++;
              stats.updatesByClient[client.name].failed++;
              break;
            case 'in_progress':
              stats.inProgressUpdates++;
              stats.updatesByClient[client.name].inProgress++;
              break;
            default:
              stats.plannedUpdates++;
          }
          
          // Compter par mois (derniers 6 mois)
          const updateDate = new Date(update.date);
          if (updateDate >= sixMonthsAgo) {
            const monthKey = `${updateDate.getFullYear()}-${(updateDate.getMonth() + 1).toString().padStart(2, '0')}`;
            if (!stats.updatesByMonth[monthKey]) {
              stats.updatesByMonth[monthKey] = 0;
            }
            stats.updatesByMonth[monthKey]++;
          }
          
          // Compter les composants mis à jour
          if (update.commits) {
            Object.keys(update.commits).forEach(component => {
              if (update.commits[component] && update.commits[component] !== '') {
                if (!stats.componentUpdates[component]) {
                  stats.componentUpdates[component] = 0;
                }
                stats.componentUpdates[component]++;
              }
            });
          }
          
          // Mises à jour récentes (30 derniers jours)
          const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
          if (updateDate >= thirtyDaysAgo) {
            stats.recentUpdates.push({
              ...update,
              clientName: client.name,
              clientId: client.id
            });
          }
        });
      }
    });
    
    // Trier les mises à jour récentes par date
    stats.recentUpdates.sort((a, b) => new Date(b.date) - new Date(a.date));
    
    return stats;
  } catch (error) {
    console.error('Erreur lors du calcul des statistiques:', error);
    return {
      totalUpdates: 0,
      completedUpdates: 0,
      failedUpdates: 0,
      inProgressUpdates: 0,
      plannedUpdates: 0,
      updatesByMonth: {},
      updatesByClient: {},
      componentUpdates: {},
      recentUpdates: []
    };
  }
};

// ==================================================
// VERSIONS DEPLOYABLES
// ==================================================

// Récupérer toutes les versions déployables
export const getDeployableVersions = async () => {
  try {
    const response = await fetch('/api/deployable-versions', {
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        bastionAuth.handleAuthError({ status: response.status });
        return { minorVersions: [] };
      }
      throw new Error('Erreur lors de la récupération des versions déployables');
    }
    return await response.json();
  } catch (error) {
    console.error('Erreur:', error);
    return { minorVersions: [] };
  }
};

// Ajouter une version mineure
export const addMinorVersion = async (minorVersion) => {
  try {
    const response = await fetch('/api/deployable-versions/minor', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(minorVersion)
    });
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        bastionAuth.handleAuthError({ status: response.status });
        return null;
      }
      const error = await response.json();
      throw new Error(error.error || 'Erreur lors de l\'ajout de la version mineure');
    }
    return await response.json();
  } catch (error) {
    console.error('Erreur:', error);
    throw error;
  }
};

// Supprimer une version mineure
export const deleteMinorVersion = async (minorId) => {
  try {
    const response = await fetch(`/api/deployable-versions/minor/${minorId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        bastionAuth.handleAuthError({ status: response.status });
        return false;
      }
      throw new Error('Erreur lors de la suppression de la version mineure');
    }
    return await response.json();
  } catch (error) {
    console.error('Erreur:', error);
    return false;
  }
};

// Ajouter une version de patch
export const addPatchVersion = async (minorId, patch) => {
  try {
    const response = await fetch(`/api/deployable-versions/minor/${minorId}/patch`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(patch)
    });
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        bastionAuth.handleAuthError({ status: response.status });
        return null;
      }
      const error = await response.json();
      throw new Error(error.error || 'Erreur lors de l\'ajout du patch');
    }
    return await response.json();
  } catch (error) {
    console.error('Erreur:', error);
    throw error;
  }
};

// Mettre à jour une version de patch
export const updatePatchVersion = async (minorId, patchId, patch) => {
  try {
    const response = await fetch(`/api/deployable-versions/minor/${minorId}/patch/${patchId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(patch)
    });
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        bastionAuth.handleAuthError({ status: response.status });
        return null;
      }
      throw new Error('Erreur lors de la mise à jour du patch');
    }
    return await response.json();
  } catch (error) {
    console.error('Erreur:', error);
    throw error;
  }
};

// Mettre à jour les notes de déploiement d'une version mineure
export const updateMinorVersionNotes = async (minorId, deploymentNotes) => {
  try {
    const response = await fetch(`/api/deployable-versions/minor/${minorId}/notes`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ deploymentNotes })
    });
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        bastionAuth.handleAuthError({ status: response.status });
        return null;
      }
      throw new Error('Erreur lors de la mise à jour des notes');
    }
    return await response.json();
  } catch (error) {
    console.error('Erreur:', error);
    throw error;
  }
};

// Supprimer une version de patch
export const deletePatchVersion = async (minorId, patchId) => {
  try {
    const response = await fetch(`/api/deployable-versions/minor/${minorId}/patch/${patchId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        bastionAuth.handleAuthError({ status: response.status });
        return false;
      }
      throw new Error('Erreur lors de la suppression du patch');
    }
    return await response.json();
  } catch (error) {
    console.error('Erreur:', error);
    return false;
  }
};

// Récupérer les clients avec problèmes (sondes non installées, BDD non optimisées, URLs non accessibles)
export const getClientsWithIssues = async () => {
  try {
    const clients = await getAllClients();
    const issues = {
      noProbesInstalled: [],
      dbNotOptimized: [],
      urlsNotAccessible: []
    };
    
    clients.forEach(client => {
      // Vérifier les sondes non installées
      let hasProbes = false;
      if (client.servers && Array.isArray(client.servers)) {
        hasProbes = client.servers.some(server => server.probeDeployed === true);
      }
      if (!hasProbes) {
        issues.noProbesInstalled.push({
          id: client.id,
          name: client.name,
          environment: client.environment,
          serverCount: client.servers ? client.servers.length : 0
        });
      }
      
      // Vérifier les BDD non optimisées
      let hasOptimizedDb = false;
      if (client.servers && Array.isArray(client.servers)) {
        hasOptimizedDb = client.servers.some(server => server.dbOptimized === true);
      }
      if (!hasOptimizedDb && client.servers && client.servers.length > 0) {
        // Vérifier qu'il y a au moins un serveur de BDD
        const hasDbServer = client.servers.some(server => 
          server.type === 'Serveur base de données' || 
          server.type === 'Serveur tout-en-un' ||
          server.mariadbVersion
        );
        if (hasDbServer) {
          issues.dbNotOptimized.push({
            id: client.id,
            name: client.name,
            environment: client.environment,
            dbServers: client.servers.filter(server => 
              server.type === 'Serveur base de données' || 
              server.type === 'Serveur tout-en-un' ||
              server.mariadbVersion
            ).length
          });
        }
      }
      
      // Vérifier les URLs non accessibles
      const urlIssues = [];
      if (client.patientPortalUrl && client.patientPortalUrlExternallyAccessible === false) {
        urlIssues.push('Portail patient non accessible');
      }
      if (client.carePortalUrl && client.carePortalUrlExternallyAccessible === false) {
        urlIssues.push('Portail soignant non accessible');
      }
      
      if (urlIssues.length > 0) {
        issues.urlsNotAccessible.push({
          id: client.id,
          name: client.name,
          environment: client.environment,
          issues: urlIssues,
          patientPortalUrl: client.patientPortalUrl,
          carePortalUrl: client.carePortalUrl
        });
      }
    });
    
    return issues;
  } catch (error) {
    console.error('Erreur lors de la récupération des problèmes clients:', error);
    return {
      noProbesInstalled: [],
      dbNotOptimized: [],
      urlsNotAccessible: []
    };
  }
};